import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { Box, Button, ButtonGroup, Container, List, TextField, Paper, Stack, IconButton, InputAdornment } from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import Styles from '../common/styles/styles';
import { validatePassword, validateConfirmPassword, validateCurrentPassword } from '../common/utils/validationUtil';
import { useUser } from '../../userContext';
import ClassificationBannerHeader from '../common/banner/classifactionBannerHeader';
import PageHeader from '../common/banner/pageHeader';

export default function ChangePassword() {
  const { userName } = useUser();

  const [formData, setFormData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false,
  });

  const [errors, setErrors] = useState({});  
  const isFormValid = Object.values(errors).filter(value => value !=='').length > 0;

  const handleClear = async () => {
    setFormData({
      currentPassword: '',
      newPassword: '',
      confirmPassword: ''
    });
  }

  const changePassword = async (event, currentPassword, newPassword) => {
    
    event.preventDefault();
    const changeData = {
      userName: userName,
      currentPassword: currentPassword,
      newPassword: newPassword,
    };
    const response = await fetch(`${process.env.REACT_APP_API}/users/change`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(changeData)
    });

    const result = await response.json();
    return result;      
    
  };

  const handleInputChange = (field) => (event) => {
      const value = event.target.value;
      setFormData({ ...formData, [field]: value });
    
      const newErrors = { ...errors };
    
      if (field === 'currentPassword') newErrors.currentPassword = validateCurrentPassword(value);
      if (field === 'newPassword') newErrors.newPassword = validatePassword(value);
      if (field === 'confirmPassword') newErrors.confirmPassword = validateConfirmPassword(formData.newPassword, value);
    
      setErrors(newErrors);
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  const handleSubmit = async (event) => {
    try {
      const result = await changePassword(event, formData.currentPassword, formData.newPassword);
      
      if (result.message === 'Success') {
        setFormData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: '',
        });
        
        // Show success toast
        toast.success('Password Updated', { autoClose: 3000 });
      } else {
        toast.error(result.message || 'Failed To Update Password', { autoClose: 3000 }); 
      }
      
    } catch (error) {
      const errorMessage = error.message || 'Failed to update password. Please try again.';
      setErrors({ submit: errorMessage });
      toast.error(errorMessage, { autoClose: 3000 });
    } 
  };

  const textFieldStyles = Styles.textFieldStyles;

  return (
    <Container sx={{ minWidth:1800, mt: 10, mb: 10 }}>
      <Box sx={{pt: 2}}>
          <ClassificationBannerHeader></ClassificationBannerHeader>
          <PageHeader title="Change Password"></PageHeader>
      </Box>
      <Box
        component={Paper}
        elevation={3}
        sx={{
          mx: 'auto',
          width: 400,
          bgcolor: '#1e1e1e',
          p: 2,
          mt: 5,
        }}
      >
        <Box component="form" onSubmit={handleSubmit}>
          <Stack spacing={2}>
            <TextField
              required
              fullWidth
              label="Current Password"
              type={showPasswords.current ? 'text' : 'password'}
              value={formData.currentPassword}
              onChange={handleInputChange('currentPassword')}
              error={!!errors.currentPassword}
              helperText= {errors.currentPassword}
              sx={textFieldStyles}
              slotProps={{
                input: {
                  autoComplete: 'current-password',
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => togglePasswordVisibility('current')}
                        edge="end"
                        sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
                      >
                        {showPasswords.current ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }
              }}
            />

            <TextField
              required
              fullWidth
              label="New Password"
              type={showPasswords.new ? 'text' : 'password'}
              value={formData.newPassword}
              onChange={handleInputChange('newPassword')}
              error={!!errors.newPassword}
              helperText= 
              {
              errors.newPassword ? 
                  errors.newPassword.map((err) => (
                  <List>{err}</List>
                  ))
              :
                  null
              }
              sx={textFieldStyles}
              slotProps={{
                input: {
                  autoComplete: 'new-password',
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => togglePasswordVisibility('new')}
                        edge="end"
                        sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
                      >
                        {showPasswords.new ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }
              }}
            />

            <TextField
              required
              fullWidth
              label="Confirm New Password"
              type={showPasswords.confirm ? 'text' : 'password'}
              value={formData.confirmPassword}
              onChange={handleInputChange('confirmPassword')}
              error={!!errors.confirmPassword}
              helperText= 
              {
              errors.confirmPassword ? 
                  errors.confirmPassword.map((err) => (
                  <List>{err}</List>
                  ))
              :
                  null
              }
              sx={textFieldStyles}
              slotProps={{
                input: {
                  autoComplete: 'new-password',
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => togglePasswordVisibility('confirm')}
                        edge="end"
                        sx={{ color: 'rgba(255, 255, 255, 0.7)' }}
                      >
                        {showPasswords.confirm ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }
              }}
            />

            <ButtonGroup 
              size="small"
              variant="contained" 
              aria-label="Basic button group" 
              fullWidth
              sx={{ mt:2 }}
            >
              <Button
                onClick={handleClear}
                type="cancel"
                variant="contained"
                sx={{
                    bgcolor: '#303F9F',
                    textTransform: 'none',
                    alignSelf: 'flex-start',
                    mt: 1,
                    '&:hover': {
                    bgcolor: '#303F9F',
                    },
                    '&:disabled': {
                    bgcolor: 'rgba(255, 255, 255, 0.12)',
                    },
                    width: '47%',
                }}
              >
                Clear 
              </Button>              
              <Button
                type="submit"                      
                variant="contained"
                disabled={isFormValid}
                sx={{
                      bgcolor: '#303F9F',
                      textTransform: 'none',
                      alignSelf: 'flex-start',
                      mt: 1,
                      ml: 4,
                      '&:hover': {
                      bgcolor: '#303F9F',
                      },
                      '&:disabled': {
                      bgcolor: 'rgba(255, 255, 255, 0.12)',
                      },
                      width: '47%',
                  }}
              >
                {'Update Password'}
              </Button>
            </ButtonGroup>
          </Stack>
        </Box>
      </Box>
    </Container>
  );
}